import {
    AssignMobileOfferRequest,
    MobileOfferAssignment,
    MobileOfferAssignmentReadiness,
    MobileOfferAssignmentReadinessStatus,
    MobileOfferAssignmentStatus,
    MobileOfferCatalogEntry,
    MobileOfferCustomer,
    MobileOfferStatus,
    MobileOfferStoreStatus,
} from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { ArrowRight } from 'lucide-react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { mobileOfferError, mobileOffersApi } from './api'
import { CompatibleOfferPicker } from './CompatibleOfferPicker'
import { CustomerAssignmentReview } from './CustomerAssignmentReview'
import { CustomerAssignmentTerms } from './CustomerAssignmentTerms'
import { CustomerOfferSummary } from './CustomerOfferSummary'
import { CustomerReassignmentDialog } from './CustomerReassignmentDialog'
import { findCustomerAssignment, matchesCustomerAssignment } from './customer-assignment-reconciliation'
import { customerDeadlineDefault, customerOfferDate, customerOfferLinkClass, customerOfferPlans, customerOfferSecondaryClass } from './customer-offer-utils'
import { canReassignOffer } from './reassignmentState'
import { OfferButton, offerFieldClass, OfferNotice, OfferSection } from './ui'
import { assignmentStores, AssignmentStoreSelection, isReviewedStoreOfferCurrent } from './assignmentStores'
import { CustomerBothStoresReview } from './CustomerBothStoresReview'

export interface CustomerAssignmentResult {
    assignment: MobileOfferAssignment
    deliveryConfirmed: boolean
}

interface CustomerAssignmentFormProps {
    customer: MobileOfferCustomer
    initialOfferId?: string
    onResult: (result: CustomerAssignmentResult | null) => void
    onHistoryRefresh: () => void
}

interface AssignmentReview {
    request: AssignMobileOfferRequest
    offer: MobileOfferCatalogEntry
}

export function CustomerAssignmentForm({ customer, initialOfferId, onResult, onHistoryRefresh }: CustomerAssignmentFormProps) {
    const [plan, setPlan] = useState<MobilePlan | ''>('')
    const [provider, setProvider] = useState<AssignmentStoreSelection>('google')
    const [bothReview, setBothReview] = useState<{ offer: MobileOfferCatalogEntry; request: Omit<AssignMobileOfferRequest, 'provider'> } | null>(null)
    const [offer, setOffer] = useState<MobileOfferCatalogEntry | null>(null)
    const [redeemBy, setRedeemBy] = useState(customerDeadlineDefault)
    const [reason, setReason] = useState('')
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    const [review, setReview] = useState<AssignmentReview | null>(null)
    const [consent, setConsent] = useState(false)
    const [retryOffer, setRetryOffer] = useState(0)
    const [checkingReadiness, setCheckingReadiness] = useState(false)
    const [availability, setAvailability] = useState<{ review: AssignmentReview; result: MobileOfferAssignmentReadiness } | null>(null)
    const [savedAssignment, setSavedAssignment] = useState<{ assignment: MobileOfferAssignment; offer: MobileOfferCatalogEntry } | null>(null)
    const [reassignmentCandidate, setReassignmentCandidate] = useState<MobileOfferAssignment | null>(null)
    const [reassigning, setReassigning] = useState(false)
    const active = useRef(true)
    const assignmentInFlight = useRef(false)
    const reviewSequence = useRef(0)

    const invalidateReview = () => {
        setBothReview(null)
        reviewSequence.current++
        setCheckingReadiness(false)
        setAvailability(null)
        setSavedAssignment(null)
        setReassignmentCandidate(null)
        setReassigning(false)
        setReview(null)
        setConsent(false)
        setError('')
    }

    useEffect(() => {
        active.current = true
        return () => {
            active.current = false
        }
    }, [])
    useEffect(() => {
        reviewSequence.current++
        setBothReview(null)
        assignmentInFlight.current = false
        setCheckingReadiness(false)
        setAvailability(null)
        setSavedAssignment(null)
        setReassignmentCandidate(null)
        setReassigning(false)
        setReview(null)
        setConsent(false)
        if (!initialOfferId) {
            setBusy(false)
            return
        }
        let cancelled = false
        setBusy(true)
        void mobileOffersApi
            .find(initialOfferId)
            .then((selected) => {
                if (cancelled) return
                const store = selected.stores.google.status === MobileOfferStoreStatus.READY ? 'google' : 'apple'
                if (selected.status !== MobileOfferStatus.ACTIVE || selected.stores[store].status !== MobileOfferStoreStatus.READY) {
                    setError('This offer is no longer available for assignment. Select another verified offer.')
                    return
                }
                setPlan(selected.terms.plan)
                setProvider(store)
                setOffer(selected)
                setError('')
            })
            .catch((failure) => {
                if (!cancelled) setError(mobileOfferError(failure))
            })
            .finally(() => {
                if (!cancelled) setBusy(false)
            })
        return () => {
            cancelled = true
        }
    }, [initialOfferId, retryOffer])

    const checkAndReview = async (candidate: AssignmentReview) => {
        const sequence = ++reviewSequence.current
        const isCurrent = () => active.current && sequence === reviewSequence.current
        setCheckingReadiness(true)
        setError('')
        setAvailability(null)
        setSavedAssignment(null)
        setReassignmentCandidate(null)
        setConsent(false)
        setReview(null)
        try {
            let { request } = candidate
            if (new Date(request.redeemBy).getTime() <= Date.now()) {
                setError('The redemption deadline has passed. Choose a future deadline.')
                return
            }
            if (request.provider === 'apple') {
                const existing = await findCustomerAssignment(mobileOffersApi.history, request.userId, (record) => matchesCustomerAssignment(record, request))
                if (!isCurrent()) return
                if (existing && canReassignOffer(existing)) {
                    setReassignmentCandidate(existing)
                    return
                }
                if (existing && existing.status !== MobileOfferAssignmentStatus.ASSIGNED) {
                    setError(`This offer already has an assignment with status ${existing.status}. Choose another offer.`)
                    return
                }
                if (existing && (existing.redeemBy !== request.redeemBy || existing.reason !== request.reason)) {
                    setSavedAssignment({ assignment: existing, offer: candidate.offer })
                    setError('An assignment already exists for this offer. Its saved deadline and reason must be used when retrying.')
                    return
                }
                if (existing) request = { ...request, expectedRevision: existing.revision ?? 0 }
                const { offerId, userId, provider, redeemBy, expectedRevision } = request
                const result = await mobileOffersApi.assignmentReadiness({ offerId, userId, provider, redeemBy, expectedRevision })
                if (!isCurrent()) return
                if (result.status !== MobileOfferAssignmentReadinessStatus.READY) {
                    setAvailability({ review: { ...candidate, request }, result })
                    return
                }
            }
            if (isCurrent()) setReview({ ...candidate, request })
        } catch (failure) {
            if (isCurrent()) setError(mobileOfferError(failure))
        } finally {
            if (isCurrent()) setCheckingReadiness(false)
        }
    }

    const prepareReview = (event: FormEvent) => {
        event.preventDefault()
        if (busy || checkingReadiness) return
        setError('')
        if (!plan) {
            setError('Choose the plan you discussed with the customer.')
            return
        }
        if (
            !offer ||
            offer.terms.plan !== plan ||
            offer.status !== MobileOfferStatus.ACTIVE ||
            !assignmentStores(provider).every((store) => offer.stores[store].status === MobileOfferStoreStatus.READY && offer.stores[store].prices?.length)
        ) {
            setError('Choose an active Gluon offer with verified regional prices for this plan and store.')
            return
        }
        const deadline = new Date(redeemBy)
        if (!redeemBy || !Number.isFinite(deadline.getTime()) || deadline.getTime() <= Date.now()) {
            setError('Choose a redeem-before deadline in the future.')
            return
        }
        if (!reason.trim() || reason.trim().length > 500) {
            setError('Enter an assignment reason of up to 500 characters.')
            return
        }
        if (provider === 'both') {
            onResult(null)
            setBothReview({ offer, request: { offerId: offer.id, userId: customer.userId, redeemBy: deadline.toISOString(), reason: reason.trim() } })
            return
        }
        void checkAndReview({
            offer,
            request: { offerId: offer.id, userId: customer.userId, provider, redeemBy: deadline.toISOString(), reason: reason.trim() },
        })
    }

    const confirm = async () => {
        if (!review || !consent || assignmentInFlight.current) return
        assignmentInFlight.current = true
        const sequence = ++reviewSequence.current
        const isCurrent = () => active.current && sequence === reviewSequence.current
        setBusy(true)
        setError('')
        onResult(null)
        const { request, offer: reviewedOffer } = review
        let assignmentStarted = false
        try {
            const current = await mobileOffersApi.find(request.offerId)
            if (!isCurrent()) return
            if (!isReviewedStoreOfferCurrent(current, reviewedOffer, request.provider)) {
                setOffer(current)
                setReview(null)
                setError('The offer configuration or prices changed. Review the current offer again before assigning it.')
                return
            }
            if (request.provider === 'apple') {
                const { offerId, userId, provider, redeemBy, expectedRevision } = request
                const result = await mobileOffersApi.assignmentReadiness({ offerId, userId, provider, redeemBy, expectedRevision })
                if (!isCurrent()) return
                if (result.status !== MobileOfferAssignmentReadinessStatus.READY) {
                    setAvailability({ review, result })
                    setReview(null)
                    setConsent(false)
                    return
                }
            }
            if (new Date(request.redeemBy).getTime() <= Date.now()) {
                setReview(null)
                setError('The redemption deadline has passed. Choose a future deadline.')
                return
            }
            assignmentStarted = true
            const assignment = await mobileOffersApi.assign(request)
            if (!isCurrent()) return
            onResult({ assignment, deliveryConfirmed: true })
            setReview(null)
            onHistoryRefresh()
        } catch (failure) {
            if (!isCurrent()) return
            if (!assignmentStarted) {
                setError(mobileOfferError(failure))
                return
            }
            let existing: MobileOfferAssignment | undefined
            try {
                existing = await findCustomerAssignment(mobileOffersApi.history, request.userId, (record) => matchesCustomerAssignment(record, request))
            } catch {
                /* Keep the outcome uncertain when reconciliation also fails. */
            }
            if (!isCurrent()) return
            setReview(null)
            if (existing) {
                onResult({ assignment: existing, deliveryConfirmed: false })
                if (existing.provider === 'apple' && existing.status === MobileOfferAssignmentStatus.ASSIGNED)
                    setSavedAssignment({ assignment: existing, offer: reviewedOffer })
                setError(
                    `${mobileOfferError(failure)} An assignment record exists (${existing.status}), but store delivery was not confirmed. Review its history before retrying the same offer.`
                )
            } else {
                setError(
                    `${mobileOfferError(failure)} The assignment outcome is not confirmed. Refresh customer history before retrying; a failed response can still leave a saved assignment.`
                )
            }
            onHistoryRefresh()
        } finally {
            if (isCurrent()) {
                assignmentInFlight.current = false
                setBusy(false)
            }
        }
    }

    return (
        <>
            <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-5">
                <div className="min-w-0 xl:col-span-3">
                    <OfferSection title="Assign an offer">
                        <form onSubmit={prepareReview} className="space-y-4">
                            <fieldset disabled={busy} className="space-y-4">
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <label className="space-y-2 text-sm font-medium">
                                        <span>1. Plan</span>
                                        <select
                                            aria-label="Assignment plan"
                                            className={offerFieldClass}
                                            value={plan}
                                            onChange={(event) => {
                                                invalidateReview()
                                                setPlan(event.target.value as MobilePlan | '')
                                                setOffer(null)
                                                setError('')
                                            }}
                                        >
                                            <option value="">Select a plan</option>
                                            {customerOfferPlans.map((item) => (
                                                <option key={item.value} value={item.value}>
                                                    {item.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="space-y-2 text-sm font-medium">
                                        <span>2. Store</span>
                                        <select
                                            aria-label="Assignment store"
                                            className={offerFieldClass}
                                            value={provider}
                                            onChange={(event) => {
                                                invalidateReview()
                                                const value = event.target.value
                                                if (value === 'apple' || value === 'google' || value === 'both') setProvider(value)
                                                setOffer(null)
                                                setError('')
                                            }}
                                        >
                                            <option value="google">Google Play</option>
                                            <option value="apple">App Store</option>
                                            <option value="both">Both stores</option>
                                        </select>
                                    </label>
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-sm font-medium">3. Offer</h3>
                                    {plan ? (
                                        <CompatibleOfferPicker
                                            key={`${plan}-${provider}`}
                                            plan={plan}
                                            provider={provider}
                                            selected={offer}
                                            onSelect={(selected) => {
                                                invalidateReview()
                                                setOffer(selected)
                                                setError('')
                                            }}
                                        />
                                    ) : (
                                        <p className={customerOfferSecondaryClass}>Select a plan to see compatible Gluon offers.</p>
                                    )}
                                </div>
                                <CustomerAssignmentTerms
                                    redeemBy={redeemBy}
                                    reason={reason}
                                    onDeadline={(value) => {
                                        invalidateReview()
                                        setRedeemBy(value)
                                    }}
                                    onReason={(value) => {
                                        invalidateReview()
                                        setReason(value)
                                    }}
                                />
                                {provider !== 'google' && (
                                    <OfferNotice>
                                        Store verification and code availability are separate. Apple code availability is checked before review and again when
                                        you confirm.
                                    </OfferNotice>
                                )}
                                <OfferNotice>
                                    This offer is for a fresh subscription. Customer eligibility is checked separately; assignment does not activate paid
                                    access.
                                </OfferNotice>
                                <OfferButton type="submit" disabled={busy || checkingReadiness}>
                                    {busy || checkingReadiness ? 'Checking…' : 'Review assignment'}
                                    <ArrowRight className="h-4 w-4" />
                                </OfferButton>
                            </fieldset>
                            {error && <OfferNotice error>{error}</OfferNotice>}
                            {reassignmentCandidate && (
                                <div className="space-y-3">
                                    <OfferNotice>
                                        This offer has a previous{' '}
                                        {reassignmentCandidate.status === MobileOfferAssignmentStatus.ASSIGNED
                                            ? 'deadline-expired'
                                            : reassignmentCandidate.status}{' '}
                                        assignment. You can review reassignment with the new deadline and reason. The server will check eligibility and code
                                        availability; previously allocated or redeemed offers cannot be reopened.
                                    </OfferNotice>
                                    <OfferButton disabled={busy || checkingReadiness || reassigning} onClick={() => setReassigning(true)}>
                                        Reassign this offer
                                        <ArrowRight className="h-4 w-4" />
                                    </OfferButton>
                                </div>
                            )}
                            {availability && (
                                <div className="space-y-3">
                                    <OfferNotice error>{availability.result.message}</OfferNotice>
                                    {availability.result.readyAfter && (
                                        <OfferNotice>
                                            Check again after {customerOfferDate(availability.result.readyAfter)} ·{' '}
                                            {Intl.DateTimeFormat().resolvedOptions().timeZone}.
                                        </OfferNotice>
                                    )}
                                    <div className="flex flex-wrap items-center gap-3">
                                        <OfferButton
                                            variant="secondary"
                                            disabled={busy || checkingReadiness}
                                            onClick={() => void checkAndReview(availability.review)}
                                        >
                                            Refresh availability
                                        </OfferButton>
                                        <Link className={customerOfferLinkClass} to={`/billing/mobile-offers/offers/${availability.review.offer.id}`}>
                                            Open offer details & code inventory
                                        </Link>
                                    </div>
                                </div>
                            )}
                            {savedAssignment && (
                                <div className="space-y-3">
                                    <OfferNotice>
                                        Saved deadline: {customerOfferDate(savedAssignment.assignment.redeemBy)}. Saved reason:{' '}
                                        {savedAssignment.assignment.reason}
                                    </OfferNotice>
                                    <OfferButton
                                        variant="secondary"
                                        disabled={busy || checkingReadiness}
                                        onClick={() => {
                                            const { assignment, offer } = savedAssignment
                                            const deadline = new Date(assignment.redeemBy)
                                            setRedeemBy(new Date(deadline.getTime() - deadline.getTimezoneOffset() * 60_000).toISOString().slice(0, 16))
                                            setReason(assignment.reason)
                                            void checkAndReview({
                                                offer,
                                                request: {
                                                    offerId: assignment.offerId,
                                                    userId: assignment.userId,
                                                    provider: assignment.provider,
                                                    redeemBy: assignment.redeemBy,
                                                    reason: assignment.reason,
                                                    expectedRevision: assignment.revision ?? 0,
                                                },
                                            })
                                        }}
                                    >
                                        Use saved assignment terms
                                    </OfferButton>
                                </div>
                            )}
                            {initialOfferId && !offer && !busy && (
                                <OfferButton variant="secondary" onClick={() => setRetryOffer((value) => value + 1)}>
                                    Reload original offer
                                </OfferButton>
                            )}
                        </form>
                    </OfferSection>
                </div>
                <aside className="min-w-0 xl:col-span-2">
                    <OfferSection title="Selected offer">
                        {offer ? (
                            <div className="space-y-5">
                                {assignmentStores(provider).map((store) => (
                                    <CustomerOfferSummary key={store} offer={offer} provider={store} />
                                ))}
                            </div>
                        ) : (
                            <p className={`${customerOfferSecondaryClass} py-6`}>
                                Choose a plan, store, and offer to review regional prices and renewal terms.
                            </p>
                        )}
                    </OfferSection>
                </aside>
            </div>
            {bothReview && (
                <CustomerBothStoresReview
                    customer={customer}
                    offer={bothReview.offer}
                    request={bothReview.request}
                    onHistoryRefresh={onHistoryRefresh}
                    onClose={() => setBothReview(null)}
                />
            )}
            {review && (
                <CustomerAssignmentReview
                    customer={customer}
                    request={review.request}
                    offer={review.offer}
                    busy={busy}
                    consent={consent}
                    error={error}
                    onConsent={setConsent}
                    onConfirm={() => void confirm()}
                    onClose={() => {
                        setReview(null)
                        setError('')
                    }}
                />
            )}
            {reassigning && reassignmentCandidate && (
                <CustomerReassignmentDialog
                    key={`${reassignmentCandidate.id}:${reassignmentCandidate.revision ?? 0}`}
                    assignment={reassignmentCandidate}
                    initialTerms={{ redeemBy, reason }}
                    onResult={(assignment, deliveryConfirmed) => {
                        onResult({ assignment, deliveryConfirmed })
                        onHistoryRefresh()
                    }}
                    onClose={() => {
                        setReassigning(false)
                        setReassignmentCandidate(null)
                        onHistoryRefresh()
                    }}
                />
            )}
        </>
    )
}
